import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Server, Monitor, Shield, Headphones, Globe, ChevronRight } from 'lucide-react';

const SECTORS = [
  'Compañías de Telefonía Fija y Celular',
  'Operadores de televisión por cable',
  'Gobierno',
  'ISP Proveedores de Internet',
  'PYMES'
];

export default function Home() {
  return (
    <div>
      {/* Hero - Dark */}
      <section className="relative overflow-hidden bg-[#010101] text-white" data-testid="home-hero-section">
        <div className="absolute inset-0" style={{background: 'radial-gradient(1200px 600px at 20% 0%, rgba(27,51,69,0.2) 0%, transparent 60%), radial-gradient(900px 500px at 80% 10%, rgba(41,95,128,0.15) 0%, transparent 55%)'}} />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <Badge className="text-xs font-medium bg-[#1B3345] text-white border-[#295F80]">Distribuidor Certificado para México</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight leading-[1.1] text-white">
                Soluciones en<br/>telecomunicaciones<br/>de clase mundial
              </h1>
              <p className="text-base md:text-lg text-gray-400 leading-relaxed max-w-xl" data-testid="home-hero-description">
                Kind Technologies es el distribuidor mayorista de AudioCodes en México, ofreciendo soluciones líderes para Microsoft Teams, SBCs, Gateways, Contact Center y Comunicaciones Unificadas. Complementamos nuestra oferta con inventario local, soporte especializado 24x7 y servicios profesionales para operadores, integradores y empresas.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/tienda">
                  <Button size="lg" className="gap-2 bg-white text-[#010101] hover:bg-gray-200 active:scale-[0.98]" data-testid="hero-store-cta">
                    Ver Tienda en Línea <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contacto">
                  <Button variant="outline" size="lg" className="gap-2 border-white/30 text-white hover:bg-white/10" data-testid="hero-contact-cta">Solicitar Cotización</Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-secondary">
                  <video
                    src="/hero-video.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    aria-label="Demostración Kind Technologies"
                    className="w-full h-full object-cover"
                    data-testid="home-hero-video"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#1B3345] rounded-lg shadow-lg p-4 border border-[#295F80]" data-testid="home-hero-stat">
                  <p className="font-display font-semibold text-2xl text-white">+15</p>
                  <p className="text-xs text-gray-400">años en el mercado</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlight - Marcas */}
      <section className="py-16 md:py-20 bg-[#f5f7f9]" data-testid="home-brands-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-xl md:text-2xl font-display font-semibold mb-2">Nuestras Marcas y Servicios</h2>
            <p className="text-muted-foreground">Integramos soluciones líder a nivel mundial en telecomunicaciones IP</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { name: 'AudioCodes', logo: '/logo-audiocodes.png', icon: Server, desc: 'Líderes en Media Gateways, SBC y Media Servers. Presencia en más de 100 países.', items: ['Session Border Controllers', 'Media Gateways', 'IP Phones', 'Teams Integration'] },
              { name: 'OneScreen', logo: '/logo-onescreen.png', icon: Monitor, desc: 'Pantallas interactivas con tecnología táctil Zero Bonded. Desde 55" hasta 105".', items: ['Pantallas Interactivas', 'Salas de Conferencia', 'Colaboración Empresarial', 'Soluciones Android'] }
            ].map(brand => (
              <Card key={brand.name} className="transition-shadow duration-150 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]" data-testid={`home-brand-card-${brand.name.toLowerCase()}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-center py-4">
                    <img src={brand.logo} alt={brand.name} className="h-16 w-auto object-contain" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-center">{brand.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{brand.desc}</p>
                  <ul className="space-y-1">
                    {brand.items.map(item => (
                      <li key={item} className="text-sm flex items-center gap-2"><ChevronRight className="h-3 w-3 text-muted-foreground" />{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/servicios"><Button variant="outline" className="gap-2" data-testid="home-view-services-btn">Ver todos los servicios <ArrowRight className="h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-16 md:py-20 bg-white" data-testid="home-clients-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-display font-semibold mb-2">Clientes que Confían en Nosotros</h2>
            <p className="text-muted-foreground">Trabajamos con las principales empresas de telecomunicaciones en México</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-axtel.png" alt="Axtel" className="h-12 w-auto object-contain" data-testid="home-client-axtel" />
            </div>
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-cemex.png" alt="Cemex" className="h-12 w-auto object-contain" data-testid="home-client-cemex" />
            </div>
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-izzi.png" alt="Izzi" className="h-12 w-auto object-contain" data-testid="home-client-izzi" />
            </div>
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-mcm.webp" alt="MCM" className="h-12 w-auto object-contain" data-testid="home-client-mcm" />
            </div>
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-telmex.png" alt="Telmex" className="h-12 w-auto object-contain" data-testid="home-client-telmex" />
            </div>
            <div className="flex items-center justify-center p-6 grayscale hover:grayscale-0 transition-all duration-300">
              <img src="/logo-totalplay.png" alt="Totalplay" className="h-12 w-auto object-contain" data-testid="home-client-totalplay" />
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {SECTORS.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20 bg-[#1B3345] text-white border-y" data-testid="home-values-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Soporte Certificado', desc: 'Instalación, soporte en garantía y extensión de garantía para todas nuestras marcas.' },
              { icon: Headphones, title: 'Atención Especializada', desc: '15 años integrando soluciones de telecomunicaciones para clientes exigentes.' },
              { icon: Globe, title: 'Stock Disponible', desc: 'Inventario disponible de productos AudioCodes y OneScreen en México.' }
            ].map(v => (
              <div key={v.title} className="space-y-3">
                <v.icon className="h-6 w-6 text-[#536976]" />
                <h3 className="font-display font-semibold text-white">{v.title}</h3>
                <p className="text-sm text-gray-300 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-16 md:py-20 bg-secondary/30" data-testid="home-mid-cta-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl md:text-2xl font-display font-semibold mb-3">¿Necesitas equipo de telecomunicaciones?</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">Explora nuestro catálogo en línea o contáctanos para una cotización personalizada.</p>
          <div className="flex justify-center gap-3">
            <Link to="/tienda"><Button size="lg" className="gap-2" data-testid="home-mid-cta-store">Ir a la Tienda <ArrowRight className="h-4 w-4" /></Button></Link>
            <Link to="/contacto"><Button variant="outline" size="lg" data-testid="home-mid-cta-contact">Contactar</Button></Link>
          </div>
        </div>
      </section>

      {/* Bottom Dark CTA - AudioCodes */}
      <section className="py-20 md:py-24 bg-[#010101] text-white relative overflow-hidden" data-testid="home-bottom-cta-section">
        <div className="absolute inset-0" style={{background: 'radial-gradient(900px 500px at 50% 0%, rgba(41,95,128,0.18) 0%, transparent 60%)'}} />
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mb-4 text-white" data-testid="home-bottom-cta-title">
            ¿Buscas productos AudioCodes en México para tu proyecto?
          </h2>
          <p className="text-base md:text-lg text-gray-400 leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="home-bottom-cta-subtitle">
            Somos el distribuidor mayorista oficial. Contáctanos y te ayudamos a encontrar la solución ideal.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contacto">
              <Button size="lg" className="gap-2 bg-white text-[#010101] hover:bg-gray-200 active:scale-[0.98]" data-testid="home-bottom-cta-contact-btn">
                Contactar ahora <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/tienda">
              <Button variant="outline" size="lg" className="gap-2 border-white/30 text-white hover:bg-white/10" data-testid="home-bottom-cta-store-btn">
                Ver catálogo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
